import type { AdminAiCoverage } from '@steado/admin-contract/ai-ops';

import { Empty, Panel } from '@/components/panel/panels';
import { TONES } from '@/components/table/tone';
import { formatPercent } from '@/lib/format';
import { cn } from '@/lib/utils';

interface CoveragePanelProps {
  readonly coverage: AdminAiCoverage;
}

/**
 * How the food-scan cascade is doing, and which catalog gaps are costing it.
 *
 * Both come from data the product already collects — coverage and unmatched
 * ingredients from the scan items themselves. Nothing is asked of anyone for
 * this screen.
 *
 * The ingredient chips sit in the closing strip rather than in a card of their
 * own, because they are the answer to the number above them: a mean coverage
 * that is low is explained by the six ingredients the catalog is missing.
 */
export function CoveragePanel({ coverage }: CoveragePanelProps) {
  const { scans, coverage: measured } = coverage;

  const rows: readonly { label: string; value: string; tone?: string }[] = [
    { label: 'Scans in the window', value: String(scans.total) },
    { label: 'Completed', value: String(scans.completed) },
    ...(scans.failed === 0
      ? [{ label: 'Failed', value: '0' }]
      : [{ label: 'Failed', value: String(scans.failed), tone: 'text-err-fg' }]),
    { label: 'Matched straight from the catalog', value: String(scans.matchedFromCatalog) },
    { label: 'Decomposed', value: String(scans.decomposed) },
    {
      label: 'Mean coverage',
      // Scoped to the decomposed scans: only those record one, and an average
      // over the rest would describe a population with none.
      value: measured.mean === null ? '—' : formatPercent(measured.mean),
    },
    ...(measured.belowThreshold === 0
      ? [{ label: `Below ${formatPercent(measured.threshold)}`, value: '0' }]
      : [
          {
            label: `Below ${formatPercent(measured.threshold)}`,
            value: String(measured.belowThreshold),
            tone: 'text-warn-fg',
          },
        ]),
  ];

  return (
    <Panel
      title="Scan coverage"
      description={`Coverage is the share of a dish’s weight the catalog could match, and only decomposed scans record one — ${String(measured.measured)} of ${String(scans.total)} here. Below ${formatPercent(measured.threshold)} the product refuses to estimate at all rather than show a confident undercount.`}
      bleed
      footer={
        <div className="flex flex-col gap-1.5">
          <span className="text-xs font-semibold tracking-[0.05em] text-muted-foreground uppercase">
            Catalog gaps worth closing next
          </span>
          {coverage.unresolvedIngredients.length === 0 ? (
            <span className="text-[12.5px] text-muted-foreground">
              Every ingredient the decomposition cascade produced in this window matched the catalog.
            </span>
          ) : (
            <div className="flex flex-wrap gap-1.5">
              {coverage.unresolvedIngredients.map((ingredient) => (
                <span
                  key={ingredient.name}
                  className="inline-flex items-center gap-1.5 rounded-xl border border-input bg-card px-2 py-1 text-[12.5px] text-foreground"
                >
                  {ingredient.name}
                  <span className="font-mono text-xs text-muted-foreground">{ingredient.occurrences}</span>
                </span>
              ))}
            </div>
          )}
        </div>
      }
    >
      <dl>
        {rows.map((row) => (
          <div key={row.label} className="flex items-baseline gap-3 border-t border-border px-[18px] py-2.5">
            <dt className="text-[13px] text-muted-foreground">{row.label}</dt>
            <dd className={cn('ml-auto font-mono text-[13px] font-medium', row.tone ?? 'text-foreground')}>
              {row.value}
            </dd>
          </div>
        ))}
      </dl>
    </Panel>
  );
}

/**
 * The dishes the model sizes wrongly, worst first.
 *
 * From the corrections users make every time they adjust a scan before logging
 * it, so it costs nobody anything to collect. Human review of the top of this
 * list is how the catalog's named portions get fixed — a dish that is
 * consistently 40% low usually means a missing or wrong `FoodPortion`.
 */
export function PortionOffendersPanel({ coverage }: CoveragePanelProps) {
  return (
    <Panel title="Portions the model reads wrongly" bleed>
      {coverage.portionOffenders.length === 0 ? (
        <div className="border-t border-border">
          <Empty
            title="No portion outliers"
            body="No dish has enough confirmed corrections in this window to judge. Corrections are recorded whenever someone adjusts a scan before logging it."
          />
        </div>
      ) : (
        <ul>
          {coverage.portionOffenders.map((dish) => (
            <li
              key={dish.dishName}
              className="grid grid-cols-[minmax(0,1fr)_88px_74px] items-center gap-3 border-t border-border px-[18px] py-2.5"
            >
              <span className="flex min-w-0 flex-col gap-px">
                <span className="truncate text-[13px] font-medium text-foreground">{dish.dishName}</span>
                <span className="text-xs text-muted-foreground">
                  {dish.medianEstimatedGrams}g guessed, {dish.medianConfirmedGrams}g logged
                </span>
              </span>
              <span
                className={cn(
                  'text-right font-mono text-[13px] font-medium',
                  // Reading a dish smaller than it is undercounts someone's day
                  // without ever looking wrong, which is the failure worth
                  // colouring differently from reading it too large.
                  dish.medianRelativeError < 0 ? 'text-err-fg' : 'text-warn-fg',
                )}
              >
                {formatSignedPercent(dish.medianRelativeError)}
              </span>
              <span className="text-right">
                <span
                  className={cn(
                    'inline-block max-w-full truncate rounded-[10px] px-1.5 text-xs leading-[19px] font-medium',
                    // Green means the dish has enough corrections that its
                    // learned median now replaces the model's guess; grey means
                    // it is still being watched.
                    TONES[dish.priorApplied ? 'ok' : 'mute'],
                  )}
                >
                  {dish.priorApplied
                    ? `prior live, ${String(dish.corrections)}×`
                    : `${String(dish.corrections)}× so far`}
                </span>
              </span>
            </li>
          ))}
        </ul>
      )}
    </Panel>
  );
}

/** "+18%" / "−31%". The sign is the point, so it is never dropped. */
function formatSignedPercent(ratio: number): string {
  const percent = Math.round(ratio * 100);
  return `${percent > 0 ? '+' : percent < 0 ? '−' : ''}${String(Math.abs(percent))}%`;
}
